import {
  CloseCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  FilePdfOutlined,
  FilterOutlined,
  MailOutlined,
} from '@ant-design/icons'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  Grid2 as Grid,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material'
import ButtonActionBar from 'components/ButtonActionBar'
import { useTheme } from '@mui/material/styles'
import Paper from 'components/@extended/Paper'
import SearchInput from 'components/SearchInput'
import SimpleTitle from 'components/SimpleTitle'
import MultiOrganizationSelector from 'components/MultiOrganizationSelector'
import useAuth from 'hooks/useAuth'
import { usePeriods } from 'hooks/usePeriods'
import { useFetchEmployeeRewardsSummaries } from 'hooks/useUserRewards'
import { useHideUserRewards, useShowUserRewards, useDeleteUserRewards } from 'hooks/useRewards'
import { useBulkDownloadRewards } from 'hooks/useBulkDownloadRewards'
import { useFetchRewardPackages } from 'hooks/useRewardPackages'
import BulkPDFDownloadDialog from 'components/BulkPDFDownloadDialog'
import BulkEmailDialog from 'components/BulkEmailDialog'
import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { getRootOrganizationId } from 'types/user-profile'
import { getCurrentRootPeriodId, findPeriodById } from 'types/periods'

const FILTER_STORAGE_KEY = 'employeeRewardsFilters'

interface PersistedFilterState {
  periodId: string
  search: string
  organizations: string[]
  packageId: string
  sort: string
  order: 'asc' | 'desc'
  includeHidden: boolean
  includeVisible: boolean
  page: number
  rowsPerPage: number
}

const EmployeeRewardsSubComponent: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const { token, profile } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [rootOrganizationId, setRootOrganizationId] = useState<string>('')
  const [downloadingPdfForUser, setDownloadingPdfForUser] = useState<string | null>(null)
  const [sendingEmailForUser, setSendingEmailForUser] = useState<string | null>(null)

  // Get color for hidden items - more muted than primary text but still readable
  const hiddenTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[400] : theme.palette.grey[500]

  // Selection state for bulk actions
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set())

  // More Filters menu state
  const [moreFiltersAnchorEl, setMoreFiltersAnchorEl] = useState<null | HTMLElement>(null)
  const moreFiltersOpen = Boolean(moreFiltersAnchorEl)

  // Hide/show/delete hooks
  const { hideUserRewards, isLoading: isHiding } = useHideUserRewards(token!)
  const { showUserRewards, isLoading: isShowing } = useShowUserRewards(token!)
  const { deleteUserRewards, isLoading: isDeleting } = useDeleteUserRewards(token!)
  const { requestBulkDownload } = useBulkDownloadRewards(token!)

  // Bulk PDF download dialog state
  const [bulkDownloadDialogOpen, setBulkDownloadDialogOpen] = useState(false)
  const [bulkDownloadRequestId, setBulkDownloadRequestId] = useState<string | null>(null)
  const [bulkDownloadRewardCount, setBulkDownloadRewardCount] = useState<number>(0)

  // Bulk email dialog state
  const [bulkEmailDialogOpen, setBulkEmailDialogOpen] = useState(false)
  const [bulkEmailRequestId, setBulkEmailRequestId] = useState<string | null>(null)
  const [bulkEmailRewardCount, setBulkEmailRewardCount] = useState<number>(0)

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('error')

  // Email confirmation dialog state
  const [emailConfirmDialogOpen, setEmailConfirmDialogOpen] = useState(false)
  const [emailConfirmUserId, setEmailConfirmUserId] = useState<string | null>(null)

  // Bulk email confirmation dialog state
  const [bulkEmailConfirmDialogOpen, setBulkEmailConfirmDialogOpen] = useState(false)

  // Check if URL has any filter params
  const hasUrlParams = useMemo(() => {
    const filterParams = [
      'periodId',
      'search',
      'organizations',
      'packageId',
      'sort',
      'order',
      'includeHidden',
      'includeVisible',
      'page',
      'rowsPerPage',
    ]
    return filterParams.some((param) => searchParams.has(param))
  }, [searchParams])

  // Load persisted state from localStorage if no URL params
  const persistedState = useMemo((): Partial<PersistedFilterState> => {
    if (hasUrlParams) return {}
    try {
      const stored = localStorage.getItem(FILTER_STORAGE_KEY)
      if (stored) {
        return JSON.parse(stored) as PersistedFilterState
      }
    } catch (e) {
      console.warn('Failed to parse persisted filter state:', e)
    }
    return {}
  }, [hasUrlParams])

  // Initialize state from URL params, falling back to persisted state, then defaults
  const [selectedPeriodId, setSelectedPeriodIdState] = useState<string>(
    searchParams.get('periodId') || persistedState.periodId || ''
  )
  const [searchTerm, setSearchTermState] = useState<string>(searchParams.get('search') || persistedState.search || '')
  const [selectedOrganizations, setSelectedOrganizationsState] = useState<string[]>(
    searchParams.get('organizations')?.split(',').filter(Boolean) || persistedState.organizations || []
  )
  const [selectedPackageId, setSelectedPackageIdState] = useState<string>(
    searchParams.get('packageId') || persistedState.packageId || ''
  )
  const [sort, setSortState] = useState<string>(searchParams.get('sort') || persistedState.sort || 'lastName')
  const [order, setOrderState] = useState<'asc' | 'desc'>(
    (searchParams.get('order') as 'asc' | 'desc') || persistedState.order || 'asc'
  )
  const [includeHidden, setIncludeHiddenState] = useState<boolean>(
    searchParams.has('includeHidden')
      ? searchParams.get('includeHidden') !== 'false'
      : (persistedState.includeHidden ?? true)
  )
  const [includeVisible, setIncludeVisibleState] = useState<boolean>(
    searchParams.has('includeVisible')
      ? searchParams.get('includeVisible') !== 'false'
      : (persistedState.includeVisible ?? true)
  )
  const [page, setPageState] = useState<number>(
    parseInt(searchParams.get('page') || '', 10) || persistedState.page || 0
  )
  const [rowsPerPage, setRowsPerPageState] = useState<number>(
    parseInt(searchParams.get('rowsPerPage') || '', 10) || persistedState.rowsPerPage || 25
  )

  // Get periods
  const { periods: periodsResponse } = usePeriods(token!, rootOrganizationId, !!rootOrganizationId && !!token)
  const periodsData = useMemo(() => periodsResponse?.periods || [], [periodsResponse])

  // Fetch reward packages for filter dropdown
  const { packages: rewardPackages } = useFetchRewardPackages(
    rootOrganizationId,
    token!,
    !!rootOrganizationId && !!token
  )

  // Get reward summaries with all filter parameters (sorting and pagination done on server)
  const {
    summaries,
    totalCount,
    isLoading: isLoadingSummaries,
    refetch: refetchSummaries,
  } = useFetchEmployeeRewardsSummaries(
    rootOrganizationId,
    selectedPeriodId,
    token!,
    !!rootOrganizationId && !!selectedPeriodId && !!token,
    {
      includeHidden,
      includeVisible,
      organizationIds: selectedOrganizations,
      packageId: selectedPackageId,
      search: searchTerm,
      sortBy: sort,
      sortOrder: order,
      limit: rowsPerPage,
      offset: page * rowsPerPage,
    }
  )

  // Functions to update both state and URL parameters
  const updateURLParams = useCallback(
    (updates: Record<string, string | string[] | boolean | number | null>) => {
      const newSearchParams = new URLSearchParams(searchParams)

      Object.entries(updates).forEach(([key, value]) => {
        if (value === null || value === '' || (Array.isArray(value) && value.length === 0)) {
          newSearchParams.delete(key)
        } else if (Array.isArray(value)) {
          newSearchParams.set(key, value.join(','))
        } else {
          newSearchParams.set(key, String(value))
        }
      })

      setSearchParams(newSearchParams, { replace: true })
    },
    [searchParams, setSearchParams]
  )

  // Persist filter state to localStorage whenever it changes
  useEffect(() => {
    const filterState: PersistedFilterState = {
      periodId: selectedPeriodId,
      search: searchTerm,
      organizations: selectedOrganizations,
      packageId: selectedPackageId,
      sort,
      order,
      includeHidden,
      includeVisible,
      page,
      rowsPerPage,
    }
    try {
      localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterState))
    } catch (e) {
      console.warn('Failed to persist filter state:', e)
    }
  }, [
    selectedPeriodId,
    searchTerm,
    selectedOrganizations,
    selectedPackageId,
    sort,
    order,
    includeHidden,
    includeVisible,
    page,
    rowsPerPage,
  ])

  useEffect(() => {
    if (profile) {
      const orgId = getRootOrganizationId(profile)
      if (orgId) {
        setRootOrganizationId(orgId)
      }
    }
  }, [profile])

  // Set default period when periods load
  useEffect(() => {
    if (periodsData.length > 0 && !selectedPeriodId) {
      const currentRootPeriodId = getCurrentRootPeriodId(periodsData)
      if (currentRootPeriodId) {
        setSelectedPeriodIdState(currentRootPeriodId)
        updateURLParams({ periodId: currentRootPeriodId })
      }
    }
  }, [periodsData, selectedPeriodId, updateURLParams])

  // Handle period selection
  const handlePeriodChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const newPeriodId = event.target.value
      setSelectedPeriodIdState(newPeriodId)
      setPageState(0)
      updateURLParams({ periodId: newPeriodId, page: '0' })
    },
    [updateURLParams]
  )

  // Get the period label for the selected period
  const selectedPeriodLabel = useMemo(() => {
    if (!selectedPeriodId || !periodsData.length) return ''
    const period = findPeriodById(periodsData, selectedPeriodId)
    return period?.label || ''
  }, [selectedPeriodId, periodsData])

  // Get root periods (top-level periods in the array)
  const rootPeriods = useMemo(() => {
    return periodsData.filter((period) => period.id)
  }, [periodsData])

  // Handle sorting
  const handleSort = useCallback(
    (property: string) => {
      const isAscending = sort === property && order === 'asc'
      const newOrder = isAscending ? 'desc' : 'asc'
      setSortState(property)
      setOrderState(newOrder)
      setPageState(0)
      updateURLParams({ sort: property, order: newOrder, page: '0' })
    },
    [sort, order, updateURLParams]
  )

  const handleChangePage = useCallback(
    (event: unknown, newPage: number) => {
      setPageState(newPage)
      updateURLParams({ page: String(newPage) })
    },
    [updateURLParams]
  )

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = Number.parseInt(event.target.value, 10)
      setRowsPerPageState(newRowsPerPage)
      setPageState(0)
      updateURLParams({ rowsPerPage: String(newRowsPerPage), page: '0' })
    },
    [updateURLParams]
  )

  const handleSearch = useCallback(
    (value: string) => {
      setSearchTermState(value)
      setPageState(0)
      updateURLParams({ search: value, page: '0' })
    },
    [updateURLParams]
  )

  const handleOrganizationChange = useCallback(
    (selected: string[]) => {
      setSelectedOrganizationsState(selected)
      setPageState(0)
      updateURLParams({ organizations: selected, page: '0' })
    },
    [updateURLParams]
  )

  const handlePackageChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const value = event.target.value
      setSelectedPackageIdState(value)
      setPageState(0)
      updateURLParams({ packageId: value, page: '0' })
    },
    [updateURLParams]
  )

  const setIncludeHidden = useCallback(
    (value: boolean) => {
      setIncludeHiddenState(value)
      setPageState(0)
      updateURLParams({ includeHidden: value, page: '0' })
    },
    [updateURLParams]
  )

  const setIncludeVisible = useCallback(
    (value: boolean) => {
      setIncludeVisibleState(value)
      setPageState(0)
      updateURLParams({ includeVisible: value, page: '0' })
    },
    [updateURLParams]
  )

  // Navigate to rewards dashboard
  const handleViewDashboard = useCallback(
    (userId: string) => {
      navigate(`/rewards/${userId}`)
    },
    [navigate]
  )

  // Navigate to edit employee rewards page
  const handleEditRewards = useCallback(
    (userId: string) => {
      navigate(`/rewards/edit/${userId}/${selectedPeriodId}`)
    },
    [navigate, selectedPeriodId]
  )

  // Selection handlers for bulk actions
  const handleSelectAll = useCallback(
    (checked: boolean) => {
      if (checked && summaries) {
        const newSelected = new Set(summaries.map((s) => s.userId))
        setSelectedEmployees(newSelected)
      } else {
        setSelectedEmployees(new Set())
      }
    },
    [summaries]
  )

  const handleSelectEmployee = useCallback((userId: string, checked: boolean) => {
    setSelectedEmployees((prev) => {
      const newSelected = new Set(prev)
      if (checked) {
        newSelected.add(userId)
      } else {
        newSelected.delete(userId)
      }
      return newSelected
    })
  }, [])

  const handleCancelSelection = useCallback(() => {
    setSelectedEmployees(new Set())
  }, [])

  // Bulk hide/show handlers
  const handleBulkHide = useCallback(async () => {
    if (!rootOrganizationId || !selectedPeriodId || selectedEmployees.size === 0) return

    try {
      await hideUserRewards({
        userIds: Array.from(selectedEmployees),
        periodId: selectedPeriodId,
        rootOrganizationId,
      })
      setSelectedEmployees(new Set())
      refetchSummaries()
    } catch (error) {
      console.error('Failed to hide user rewards:', error)
    }
  }, [rootOrganizationId, selectedPeriodId, selectedEmployees, hideUserRewards, refetchSummaries])

  const handleBulkShow = useCallback(async () => {
    if (!rootOrganizationId || !selectedPeriodId || selectedEmployees.size === 0) return

    try {
      await showUserRewards({
        userIds: Array.from(selectedEmployees),
        periodId: selectedPeriodId,
        rootOrganizationId,
      })
      setSelectedEmployees(new Set())
      refetchSummaries()
    } catch (error) {
      console.error('Failed to show user rewards:', error)
    }
  }, [rootOrganizationId, selectedPeriodId, selectedEmployees, showUserRewards, refetchSummaries])

  const handleBulkDelete = useCallback(async () => {
    if (!rootOrganizationId || !selectedPeriodId || selectedEmployees.size === 0) return

    try {
      await deleteUserRewards({
        userIds: Array.from(selectedEmployees),
        periodId: selectedPeriodId,
        rootOrganizationId,
      })
      setSelectedEmployees(new Set())
      refetchSummaries()
    } catch (error) {
      console.error('Failed to delete user rewards:', error)
    }
  }, [rootOrganizationId, selectedPeriodId, selectedEmployees, deleteUserRewards, refetchSummaries])

  const handleBulkDownload = useCallback(async () => {
    if (!rootOrganizationId || !selectedPeriodId || selectedEmployees.size === 0 || !summaries) return

    try {
      // Build userRewards array with id and revision from selected employees
      const userRewards = Array.from(selectedEmployees)
        .map((userId) => {
          const summary = summaries.find((s) => s.userId === userId)
          if (!summary) return null
          // Validate that id and revision exist
          if (!summary.id || summary.revision === undefined) {
            console.error('Missing id or revision for user:', userId, summary)
            return null
          }
          return {
            id: summary.id,
            revision: summary.revision,
          }
        })
        .filter((ur): ur is { id: string; revision: number } => ur !== null)

      // Check if we have valid user rewards
      if (userRewards.length === 0) {
        console.error('No valid user rewards found. The data is missing id and revision fields.')
        setSnackbarSeverity('error')
        setSnackbarMessage(
          'Unable to download PDFs. The reward data is incomplete. Please refresh the page and try again. If the problem persists, the API server may need to be restarted.'
        )
        setSnackbarOpen(true)
        return
      }

      const response = await requestBulkDownload({
        rootOrganizationId,
        periodId: selectedPeriodId,
        userRewards,
      })

      if (response) {
        // Open the dialog with the request ID
        setBulkDownloadRequestId(response.requestId)
        setBulkDownloadRewardCount(response.rewardCount)
        setBulkDownloadDialogOpen(true)
      }
    } catch (error) {
      console.error('Failed to request bulk download:', error)
    }
  }, [rootOrganizationId, selectedPeriodId, selectedEmployees, summaries, requestBulkDownload])

  const handleCloseBulkDownloadDialog = useCallback(() => {
    setBulkDownloadDialogOpen(false)
    // Clear selection after closing dialog
    setSelectedEmployees(new Set())
  }, [])

  const handleCloseBulkEmailDialog = useCallback(() => {
    setBulkEmailDialogOpen(false)
    // Clear selection after closing dialog
    setSelectedEmployees(new Set())
  }, [])

  const isAllSelected = (summaries?.length ?? 0) > 0 && selectedEmployees.size === (summaries?.length ?? 0)
  const isIndeterminate = selectedEmployees.size > 0 && selectedEmployees.size < (summaries?.length ?? 0)

  // Check if selection contains hidden/visible items
  const selectedHasHidden = useMemo(() => {
    if (!summaries) return false
    return Array.from(selectedEmployees).some((userId) => {
      const summary = summaries.find((s) => s.userId === userId)
      return summary?.isHidden
    })
  }, [selectedEmployees, summaries])

  const selectedHasVisible = useMemo(() => {
    if (!summaries) return false
    return Array.from(selectedEmployees).some((userId) => {
      const summary = summaries.find((s) => s.userId === userId)
      return summary && !summary.isHidden
    })
  }, [selectedEmployees, summaries])

  // Download PDF for a specific user using server-side rendering
  const handleDownloadPdf = useCallback(
    (userId: string) => {
      if (!token || !summaries) return

      // Find the summary to get the id and revision
      const summary = summaries.find((s) => s.userId === userId)
      if (!summary || !summary.id || summary.revision === undefined) {
        console.error('Could not find user reward id and revision for user:', userId)
        setSnackbarSeverity('error')
        setSnackbarMessage('Unable to download PDF. Please refresh the page and try again.')
        setSnackbarOpen(true)
        return
      }

      // Set loading state
      setDownloadingPdfForUser(userId)

      // Use the server-side PDF endpoint
      const apiUrl = `${process.env.REACT_APP_API_SERVER_URL}rewards/user-rewards/${summary.id}/${summary.revision}/pdf`

      // Fetch the JSON response which contains the presigned URL
      fetch(apiUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((response) => response.json())
        .then((data: { url: string; fileName: string }) => {
          // Open the presigned URL - S3 response-content-disposition header will trigger download
          window.open(data.url, '_blank')
        })
        .catch((error) => {
          console.error('Failed to download PDF:', error)
          setSnackbarSeverity('error')
          setSnackbarMessage('Failed to download PDF. Please try again.')
          setSnackbarOpen(true)
        })
        .finally(() => {
          // Clear loading state
          setDownloadingPdfForUser(null)
        })
    },
    [token, summaries]
  )

  // Handle email button click - opens confirmation dialog
  const handleSendEmail = useCallback((userId: string) => {
    setEmailConfirmUserId(userId)
    setEmailConfirmDialogOpen(true)
  }, [])

  // Actually send the email after confirmation
  const handleConfirmSendEmail = useCallback(() => {
    if (!token || !summaries || !emailConfirmUserId) return

    // Close the confirmation dialog
    setEmailConfirmDialogOpen(false)

    // Find the summary to get the id and revision
    const summary = summaries.find((s) => s.userId === emailConfirmUserId)
    if (!summary || !summary.id || summary.revision === undefined) {
      console.error('Could not find user reward id and revision for user:', emailConfirmUserId)
      setSnackbarSeverity('error')
      setSnackbarMessage('Unable to send email. Please refresh the page and try again.')
      setSnackbarOpen(true)
      setEmailConfirmUserId(null)
      return
    }

    // Set loading state
    setSendingEmailForUser(emailConfirmUserId)

    // Use the email endpoint
    const apiUrl = `${process.env.REACT_APP_API_SERVER_URL}rewards/user-rewards/${summary.id}/${summary.revision}/email`

    // Send email request
    fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((data: { message: string; email: string }) => {
        setSnackbarSeverity('success')
        setSnackbarMessage(`Email sent successfully to ${data.email}`)
        setSnackbarOpen(true)
      })
      .catch((error) => {
        console.error('Failed to send email:', error)
        setSnackbarSeverity('error')
        setSnackbarMessage('Failed to send email. Please try again.')
        setSnackbarOpen(true)
      })
      .finally(() => {
        // Clear loading state
        setSendingEmailForUser(null)
        setEmailConfirmUserId(null)
      })
  }, [token, summaries, emailConfirmUserId])

  // Cancel email confirmation
  const handleCancelSendEmail = useCallback(() => {
    setEmailConfirmDialogOpen(false)
    setEmailConfirmUserId(null)
  }, [])

  // Handle bulk email button click - opens confirmation dialog
  const handleBulkEmail = useCallback(() => {
    if (!token || !summaries || !rootOrganizationId || !selectedPeriodId) {
      console.error('Missing required data for bulk email')
      return
    }

    // Filter selected employees to get their IDs and revisions
    const selectedSummaries = summaries.filter((s) => selectedEmployees.has(s.userId))

    if (selectedSummaries.length === 0) {
      setSnackbarSeverity('error')
      setSnackbarMessage('No employees selected')
      setSnackbarOpen(true)
      return
    }

    // Check if all selected summaries have id and revision
    const allValid = selectedSummaries.every((s) => s.id && s.revision !== undefined)
    if (!allValid) {
      setSnackbarSeverity('error')
      setSnackbarMessage('Unable to process request. Please refresh the page and try again.')
      setSnackbarOpen(true)
      return
    }

    // Open confirmation dialog
    setBulkEmailConfirmDialogOpen(true)
  }, [token, summaries, rootOrganizationId, selectedPeriodId, selectedEmployees])

  // Actually send bulk emails after confirmation
  const handleConfirmBulkEmail = useCallback(() => {
    if (!token || !summaries || !rootOrganizationId || !selectedPeriodId) {
      return
    }

    // Close confirmation dialog
    setBulkEmailConfirmDialogOpen(false)

    // Filter selected employees to get their IDs and revisions
    const selectedSummaries = summaries.filter((s) => selectedEmployees.has(s.userId))

    // Map to user reward identifiers
    const userRewards = selectedSummaries.map((s) => ({
      id: s.id!,
      revision: s.revision!,
    }))

    // Make API request to bulk email endpoint
    const apiUrl = `${process.env.REACT_APP_API_SERVER_URL}rewards/bulk-email`

    fetch(apiUrl, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        rootOrganizationId,
        periodId: selectedPeriodId,
        userRewards,
      }),
    })
      .then((response) => response.json())
      .then((data: { requestId: string; message: string; rewardCount: number }) => {
        // Open the bulk email dialog
        setBulkEmailRequestId(data.requestId)
        setBulkEmailRewardCount(data.rewardCount)
        setBulkEmailDialogOpen(true)
      })
      .catch((error) => {
        console.error('Failed to request bulk email:', error)
        setSnackbarSeverity('error')
        setSnackbarMessage('Failed to request bulk email. Please try again.')
        setSnackbarOpen(true)
      })
  }, [token, summaries, rootOrganizationId, selectedPeriodId, selectedEmployees])

  // Cancel bulk email confirmation
  const handleCancelBulkEmail = useCallback(() => {
    setBulkEmailConfirmDialogOpen(false)
  }, [])

  // Clear all filters
  const handleClearFilters = useCallback(() => {
    setSelectedOrganizationsState([])
    setSelectedPackageIdState('')
    setSearchTermState('')
    setIncludeHiddenState(true)
    setIncludeVisibleState(true)
    setSortState('lastName')
    setOrderState('asc')
    setPageState(0)
    setRowsPerPageState(25)
    // Clear persisted filter state
    localStorage.removeItem(FILTER_STORAGE_KEY)
    // Clear all URL parameters
    updateURLParams({
      organizations: [],
      packageId: '',
      search: '',
      includeHidden: true,
      includeVisible: true,
      sort: 'lastName',
      order: 'asc',
      page: '0',
      rowsPerPage: '25',
    })
  }, [updateURLParams])

  const isLoading = isLoadingSummaries

  // Build package options - must be before any early returns to maintain hook order
  const packageOptions = useMemo(() => {
    const options: { id: string; label: string }[] = [{ id: '', label: 'All Packages' }]
    if (rewardPackages) {
      for (const pkg of rewardPackages) {
        if (pkg.id) {
          options.push({ id: pkg.id, label: pkg.name })
        }
      }
    }
    return options
  }, [rewardPackages])

  if (!selectedPeriodId && periodsData.length > 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Grid container pb={2}>
        <Grid size={{ xs: 12 }}>
          <SimpleTitle title="Employee Rewards" />
        </Grid>
      </Grid>

      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid size={{ xs: 3 }}>
          <SearchInput
            id="employees-search"
            onSearch={handleSearch}
            initialValue={searchTerm}
            labelBackgroundColor={theme.palette.background.default}
          />
        </Grid>
        <Grid size={{ xs: 9 }}>
          <Box display="flex" gap={2} justifyContent="flex-end" alignItems="center">
            <FormControl variant="outlined" sx={{ width: '250px' }}>
              <InputLabel
                shrink
                variant="outlined"
                sx={{
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.default,
                  },
                }}
              >
                Package
              </InputLabel>
              <Select
                value={selectedPackageId}
                label="Package"
                onChange={handlePackageChange}
                style={{ borderRadius: '21px' }}
                displayEmpty
              >
                {packageOptions.map((pkg) => (
                  <MenuItem key={pkg.id} value={pkg.id}>
                    {pkg.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" sx={{ width: '200px' }}>
              <InputLabel
                shrink
                variant="outlined"
                sx={{
                  '&.MuiInputLabel-shrink': {
                    background: theme.palette.background.default,
                  },
                }}
              >
                Period
              </InputLabel>
              <Select
                labelId="period-select-label"
                id="period-select"
                variant="outlined"
                value={selectedPeriodId}
                label="Period"
                style={{ borderRadius: '21px' }}
                onChange={handlePeriodChange}
              >
                {rootPeriods.map((period) => (
                  <MenuItem key={period.id!} value={period.id!}>
                    {period.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {profile && (
              <MultiOrganizationSelector
                userProfile={profile}
                selectedOrganizationIds={selectedOrganizations}
                handleChange={handleOrganizationChange}
                selectAll
                labelBackgroundColor={theme.palette.background.default}
                width="300px"
              />
            )}
            <Tooltip title="More Filters">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<FilterOutlined />}
                onClick={(e) => setMoreFiltersAnchorEl(e.currentTarget)}
                sx={{
                  height: '40px',
                  minWidth: '140px',
                  borderColor: !includeHidden || !includeVisible ? theme.palette.primary.main : undefined,
                }}
              >
                More Filters
              </Button>
            </Tooltip>
            <Menu
              anchorEl={moreFiltersAnchorEl}
              open={moreFiltersOpen}
              onClose={() => setMoreFiltersAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              PaperProps={{
                sx: { minWidth: '280px', mt: 1 },
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="overline" sx={{ display: 'block', color: 'text.secondary' }}>
                  Visibility Filters
                </Typography>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeVisible}
                        onChange={(e) => setIncludeVisible(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Visible"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={includeHidden}
                        onChange={(e) => setIncludeHidden(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Show Hidden"
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem',
                      },
                    }}
                  />
                </Stack>
              </Box>
            </Menu>
            <Tooltip title="Clear Filters">
              <IconButton color="primary" onClick={handleClearFilters}>
                <CloseCircleOutlined />
              </IconButton>
            </Tooltip>
          </Box>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (summaries?.length ?? 0) === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography color="textSecondary">
              {searchTerm ? 'No employees match your search' : 'No employees with rewards in this period'}
            </Typography>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 400px)', minHeight: '300px' }}>
              <Table stickyHeader size="small" aria-label="employees table">
                <TableHead
                  sx={{
                    '& th': {
                      borderTop: `1px solid ${theme.palette.divider}`,
                      borderBottom: `2px solid ${theme.palette.divider} !important`,
                    },
                  }}
                >
                  <TableRow>
                    <TableCell sx={{ position: 'sticky !important', width: 50, minWidth: 50 }} padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 220, position: 'sticky !important' }}>
                      <TableSortLabel
                        active={sort === 'lastName'}
                        direction={sort === 'lastName' ? order : 'asc'}
                        onClick={() => handleSort('lastName')}
                      >
                        Employee
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: 180, position: 'sticky !important' }}>Team</TableCell>
                    <TableCell sx={{ width: 150, position: 'sticky !important' }}>Period</TableCell>
                    <TableCell align="center" sx={{ width: 100, position: 'sticky !important' }}>
                      <TableSortLabel
                        active={sort === 'rewardCount'}
                        direction={sort === 'rewardCount' ? order : 'asc'}
                        onClick={() => handleSort('rewardCount')}
                      >
                        Rewards
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ width: 150, position: 'sticky !important' }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(summaries ?? []).map((summary) => (
                    <TableRow
                      key={summary.userId}
                      sx={{
                        py: 3,
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={selectedEmployees.has(summary.userId)}
                          onChange={(e) => handleSelectEmployee(summary.userId, e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            fontWeight="medium"
                            sx={{ color: summary.isHidden ? hiddenTextColor : 'text.primary' }}
                          >
                            {summary.firstName} {summary.lastName}
                          </Typography>
                          {summary.isHidden && (
                            <Tooltip title="Hidden">
                              <EyeInvisibleOutlined style={{ color: hiddenTextColor, fontSize: 14 }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: summary.isHidden ? hiddenTextColor : 'text.primary' }}>
                        {summary.organizationName}
                      </TableCell>
                      <TableCell sx={{ color: summary.isHidden ? hiddenTextColor : 'text.primary' }}>
                        {selectedPeriodLabel}
                      </TableCell>
                      <TableCell align="center" sx={{ color: summary.isHidden ? hiddenTextColor : 'text.primary' }}>
                        {summary.rewardCount}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="View rewards dashboard">
                          <IconButton size="medium" onClick={() => handleViewDashboard(summary.userId)}>
                            <EyeOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit rewards">
                          <IconButton size="medium" onClick={() => handleEditRewards(summary.userId)}>
                            <EditOutlined />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="medium"
                            onClick={() => handleDownloadPdf(summary.userId)}
                            disabled={downloadingPdfForUser === summary.userId}
                          >
                            {downloadingPdfForUser === summary.userId ? (
                              <CircularProgress size={18} />
                            ) : (
                              <FilePdfOutlined />
                            )}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Send via Email">
                          <IconButton
                            size="medium"
                            onClick={() => handleSendEmail(summary.userId)}
                            disabled={sendingEmailForUser === summary.userId}
                          >
                            {sendingEmailForUser === summary.userId ? <CircularProgress size={18} /> : <MailOutlined />}
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Divider />
            <Box display="flex" alignItems="center" justifyContent="flex-end" width="100%" sx={{ px: '20px' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100, 200]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Box>
          </>
        )}
      </Paper>

      {/* Bulk Action Bar */}
      <ButtonActionBar show={selectedEmployees.size > 0} maxWidth="800px" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Box sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
            {selectedEmployees.size} {selectedEmployees.size === 1 ? 'employee' : 'employees'} selected
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<CloseCircleOutlined />}
            onClick={handleCancelSelection}
            disabled={isHiding || isShowing || isDeleting}
          >
            Cancel
          </Button>
          {selectedHasVisible && (
            <Tooltip title="Make selected employee rewards hidden">
              <span>
                <Button variant="outlined" onClick={handleBulkHide} disabled={isHiding || isShowing || isDeleting}>
                  {isHiding ? 'Making Hidden...' : 'Make Hidden'}
                </Button>
              </span>
            </Tooltip>
          )}
          {selectedHasHidden && (
            <Tooltip title="Make selected employee rewards visible">
              <span>
                <Button variant="outlined" onClick={handleBulkShow} disabled={isHiding || isShowing || isDeleting}>
                  {isShowing ? 'Making Visible...' : 'Make Visible'}
                </Button>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Delete selected employee rewards">
            <span>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteOutlined />}
                onClick={handleBulkDelete}
                disabled={isHiding || isShowing || isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Download PDF for each selected employee">
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<FilePdfOutlined />}
                onClick={handleBulkDownload}
                disabled={selectedEmployees.size === 0 || isHiding || isShowing || isDeleting}
              >
                Bulk Download PDFs
              </Button>
            </span>
          </Tooltip>
          <Tooltip title="Email PDF to each selected employee">
            <span>
              <Button
                variant="contained"
                color="primary"
                startIcon={<MailOutlined />}
                onClick={handleBulkEmail}
                disabled={selectedEmployees.size === 0 || isHiding || isShowing || isDeleting}
              >
                Bulk Email PDFs
              </Button>
            </span>
          </Tooltip>
        </Box>
      </ButtonActionBar>

      {/* Bulk PDF Download Dialog */}
      <BulkPDFDownloadDialog
        open={bulkDownloadDialogOpen}
        onClose={handleCloseBulkDownloadDialog}
        requestId={bulkDownloadRequestId}
        rewardCount={bulkDownloadRewardCount}
      />

      {/* Bulk Email Dialog */}
      <BulkEmailDialog
        open={bulkEmailDialogOpen}
        onClose={handleCloseBulkEmailDialog}
        requestId={bulkEmailRequestId}
        rewardCount={bulkEmailRewardCount}
      />

      {/* Email Confirmation Dialog */}
      <Dialog open={emailConfirmDialogOpen} onClose={handleCancelSendEmail} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <MailOutlined style={{ fontSize: 48, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Send Reward Statement
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confirm that you want to email this statement
            </Typography>
          </Box>

          {emailConfirmUserId && summaries && (
            <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                Employee
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                {summaries.find((s) => s.userId === emailConfirmUserId)?.firstName}{' '}
                {summaries.find((s) => s.userId === emailConfirmUserId)?.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Email will be sent to their registered address
              </Typography>
            </Box>
          )}

          <Alert severity="warning" sx={{ mb: 2 }}>
            This statement contains confidential compensation information.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCancelSendEmail}>Cancel</Button>
          <Button onClick={handleConfirmSendEmail} variant="contained" startIcon={<MailOutlined />}>
            Send Email
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Email Confirmation Dialog */}
      <Dialog open={bulkEmailConfirmDialogOpen} onClose={handleCancelBulkEmail} maxWidth="sm" fullWidth>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <MailOutlined style={{ fontSize: 48, color: theme.palette.primary.main }} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
              Send Bulk Reward Statements
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Confirm bulk email delivery
            </Typography>
          </Box>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Recipients
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
              {selectedEmployees.size}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {selectedEmployees.size === 1 ? 'employee' : 'employees'} will receive their reward statement via email
            </Typography>
          </Box>

          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              This will send confidential compensation data
            </Typography>
            <Typography variant="body2">
              Each employee will receive their total rewards statement containing sensitive compensation information.
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={handleCancelBulkEmail}>Cancel</Button>
          <Button onClick={handleConfirmBulkEmail} variant="contained" startIcon={<MailOutlined />}>
            Send {selectedEmployees.size} {selectedEmployees.size === 1 ? 'Email' : 'Emails'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={8000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  )
}

const EmployeeRewardsPage: React.FC = () => {
  return (
    <Suspense fallback={<CircularProgress />}>
      <EmployeeRewardsSubComponent />
    </Suspense>
  )
}

export default EmployeeRewardsPage
